import { Link, router } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Notice } from "@/components/ui/notice"
import { clockStamp } from "@/lib/duration"
import {
  cancelAdminCallPath,
  endAdminCallPath,
} from "@/pages/admin/calls/paths"
import type { ActiveCall, SessionState } from "@/pages/admin/calls/types"

import { AgendaMarks } from "./agenda-marks"
import { IN_CALL, PRESSED_ECHO, reasonLabel } from "./copy"
import { MemoGuide } from "./memo-guide"
import { SessionPill } from "./session-pill"
import { requestId, usePressedEcho } from "./use-pressed-echo"
import { useSessionPoll } from "./use-session-poll"
import { useTickingSeconds } from "./use-ticking-seconds"

/*
 * The in-call panel replaces the card's call block in place — same slot, no
 * navigation. The pill says where the session is; the buttons are the two the
 * gem accepts, valid only where its rules say:
 *
 *   End call   dialing_contact · in_call        hangs up the school, keeps you on for the memo
 *   Cancel     dialing_staffer … in_call         abandons before the school rang, interrupts after
 *   Cancel memo memo_prompting · recording_memo · redialing_staffer
 *
 * Every press carries a request_id the gem dedupes on, and the panel echoes
 * the press for two seconds so a double click reads as one. Updates arrive by
 * a two-second partial reload while the session is active.
 */
const END_STATES: SessionState[] = ["dialing_contact", "in_call"]
const CANCEL_STATES: SessionState[] = [
  "dialing_staffer",
  "awaiting_confirmation",
  "dialing_contact",
  "in_call",
]
const CANCEL_MEMO_STATES: SessionState[] = [
  "memo_prompting",
  "recording_memo",
  "redialing_staffer",
]
const MARK_STATES: SessionState[] = [
  "awaiting_confirmation",
  "dialing_contact",
  "in_call",
]

export function InCallPanel({
  call,
  pollPath,
}: {
  call: ActiveCall
  /** The page's canonical GET, reloaded with `only: ["active_call"]`. */
  pollPath: string
}) {
  const { state } = call
  useSessionPoll({
    enabled: call.kind === "active",
    path: pollPath,
    only: ["active_call"],
  })
  const { pressed, press } = usePressedEcho()
  const liveTalk = useTickingSeconds(
    state === "in_call" ? call.bridged_at : null,
  )
  const memoSeconds = useTickingSeconds(
    state === "recording_memo" ? call.memo_started_at : null,
  )
  const talkSeconds = state === "in_call" ? liveTalk : call.talk_seconds

  const act = (name: "end" | "cancel" | "cancel_memo") =>
    press(name, () => {
      const path =
        name === "end"
          ? endAdminCallPath(call.id)
          : cancelAdminCallPath(call.id)
      router.post(
        path,
        { request_id: requestId() },
        { preserveScroll: true, preserveState: true, only: ["active_call"] },
      )
    })

  const showEnd = END_STATES.includes(state)
  const showCancel = state === "created" || CANCEL_STATES.includes(state)
  const showCancelMemo = CANCEL_MEMO_STATES.includes(state)
  const busy = pressed !== null
  const openItems = call.agenda
    .filter((i) => i.state === "open")
    .map((i) => i.label)
  const reason = reasonLabel(call.failure_reason, call.failure_reason_label)

  return (
    <section
      aria-label={`Call with ${call.school.name}`}
      aria-live="polite"
      className="card flex flex-col gap-4 px-5 py-4"
    >
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SessionPill
            state={state}
            contactName={call.contact_first_name}
            ringing={call.contact_ringing}
            seconds={state === "recording_memo" ? memoSeconds : talkSeconds}
            size="lg"
          />
          {call.bridged_at && state !== "recording_memo" && (
            <span className="notation">
              bridged {clockStamp(call.bridged_at)}
            </span>
          )}
        </div>
        <p className="text-tertiary text-xs">
          {call.school.name}
          {call.contact_first_name ? ` · ${call.contact_first_name}` : ""} ·
          attempt {call.attempt.step} of {call.attempt.of}
        </p>
      </div>

      {(showEnd || showCancel) && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            {showEnd && (
              <Button
                size="lg"
                color={state === "in_call" ? "primary" : "secondary"}
                isDisabled={busy}
                onPress={() => act("end")}
                className="pointer-coarse:h-11"
              >
                {pressed === "end" ? PRESSED_ECHO.end : "End call"}
              </Button>
            )}
            {showCancel && (
              <Button
                size="lg"
                color="secondary"
                isDisabled={busy || state === "created"}
                onPress={() => act("cancel")}
                className="pointer-coarse:h-11"
              >
                {pressed === "cancel" ? PRESSED_ECHO.cancel : "Cancel"}
              </Button>
            )}
          </div>
          {showEnd && (
            <p className="text-tertiary text-xs">{IN_CALL.buttonHint}</p>
          )}
        </div>
      )}

      {MARK_STATES.includes(state) && call.agenda.length > 0 && (
        <AgendaMarks
          callId={call.id}
          items={call.agenda}
          head={IN_CALL.marksHead}
        />
      )}

      {(state === "memo_prompting" || state === "recording_memo") && (
        <MemoGuide
          recording={state === "recording_memo"}
          seconds={memoSeconds}
          maxSeconds={call.memo_max_seconds}
          openItems={openItems}
          glossary={call.glossary}
          cancelLabel={
            pressed === "cancel_memo" ? PRESSED_ECHO.cancel_memo : "Cancel memo"
          }
          cancelDisabled={busy}
          onCancel={() => act("cancel_memo")}
        />
      )}

      {state === "redialing_staffer" && (
        <div className="flex flex-col gap-3">
          <p className="text-secondary text-sm">{IN_CALL.redialing}</p>
          {openItems.length > 0 && (
            <div className="well flex flex-col gap-1 px-3.5 py-2.5">
              <span className="eyebrow">{IN_CALL.stillToCover}</span>
              <span className="text-secondary text-sm">
                {openItems.join(" · ")}
              </span>
            </div>
          )}
          {showCancelMemo && (
            <div>
              <Button
                size="lg"
                color="secondary"
                isDisabled={busy}
                onPress={() => act("cancel_memo")}
                className="pointer-coarse:h-11"
              >
                {pressed === "cancel_memo"
                  ? PRESSED_ECHO.cancel_memo
                  : "Cancel memo"}
              </Button>
            </div>
          )}
        </div>
      )}

      {state === "processing" && (
        <p className="text-tertiary text-sm">{IN_CALL.processing}</p>
      )}

      {state === "completed" && (
        <p className="text-secondary text-sm">
          The summary is ready.{" "}
          {call.review_path ? (
            <Link
              href={call.review_path}
              className="text-brand-secondary hover:text-brand-secondary_hover"
            >
              Review this call
            </Link>
          ) : (
            "The review is on its way."
          )}
        </p>
      )}

      {state === "needs_attention" && (
        <div className="flex flex-col gap-2">
          <Notice tone="caution" role="status">
            {call.failure_reason === "memo_lost" ? IN_CALL.memoLost : reason}
          </Notice>
          <p className="text-tertiary text-xs">{IN_CALL.memoLostAside}</p>
          {call.review_path && (
            <Link
              href={call.review_path}
              className="text-brand-secondary hover:text-brand-secondary_hover text-sm"
            >
              Open this call
            </Link>
          )}
        </div>
      )}

      {call.kind === "terminal" && state !== "completed" && reason && (
        <p className="text-tertiary text-sm">{reason}.</p>
      )}

      {call.agenda.length > 0 &&
        !MARK_STATES.includes(state) &&
        state !== "memo_prompting" &&
        state !== "recording_memo" &&
        state !== "redialing_staffer" && (
          <AgendaMarks
            callId={call.id}
            items={call.agenda}
            head="Agenda marks"
            readOnly
          />
        )}
    </section>
  )
}
